import { onCocoEvent, emitCocoEvent, COCO_EVENTS } from '@theme/theme-events';

const CE_NAME = 'coco-qty';

class CocoQty extends HTMLElement {
  #abort = null;
  #unsubUpdated = null;
  #unsubBoundaries = null;
  #unsubRules = null;
  #changeEvent = null;
  #buttons = [];

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input?.value;
  }

  connectedCallback() {
    if (!this.input) return;

    this.#abort = new AbortController();
    this.#buttons = Array.from(this.querySelectorAll('button'));
    this.#changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', () => this.#onInputChange());
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()));

    this.#buttons.forEach((button) => {
      button.addEventListener('click', (event) => this.#onButtonClick(event), {
        signal: this.#abort.signal,
      });
    });

    this.#syncButtons();
    this.#unsubUpdated = onCocoEvent(COCO_EVENTS.quantityUpdated, () => this.#syncButtons());
    this.#unsubBoundaries = onCocoEvent(COCO_EVENTS.quantityBoundaries, (event) =>
      this.#applyBoundaries(event),
    );
    this.#unsubRules = onCocoEvent(COCO_EVENTS.quantityRules, (event) => this.#applyRules(event));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#unsubUpdated?.();
    this.#unsubBoundaries?.();
    this.#unsubRules?.();
  }

  #onButtonClick(event) {
    event.preventDefault();
    if (this.input.disabled) return;

    const previousValue = this.input.value;
    const button = event.currentTarget;

    if (button.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min'), 10) > parseInt(this.input.step, 10) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.#changeEvent);
    }

    if (this.input.getAttribute('data-min') === previousValue && button.name === 'minus') {
      this.input.value = parseInt(this.input.min, 10);
    }
  }

  #onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min, 10);
    }
    this.#syncButtons();
  }

  #syncButtons() {
    const value = parseInt(this.input.value, 10);

    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      buttonMinus?.toggleAttribute('disabled', value <= parseInt(this.input.min, 10));
    }

    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      buttonPlus?.toggleAttribute('disabled', value >= parseInt(this.input.max, 10));
    }
  }

  #applyRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity-input', '.quantity-rules', '.quantity-label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    if (!quantityForm || !quantityFormUpdated) return;

    for (const selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity-input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (const attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  #applyBoundaries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const cartQuantity = this.input.hasAttribute('data-cart-quantity')
      ? parseInt(this.input.getAttribute('data-cart-quantity'), 10)
      : 0;
    const dataMin = this.input.hasAttribute('data-min')
      ? parseInt(this.input.getAttribute('data-min'), 10)
      : 1;
    const dataMax = this.input.hasAttribute('data-max')
      ? parseInt(this.input.getAttribute('data-max'), 10)
      : null;
    const step = this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step'), 10) : 1;

    let min = dataMin;
    const max = dataMax === null ? dataMax : dataMax - cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (cartQuantity >= dataMin) min = Math.min(min, step);

    this.input.min = min;
    if (max) {
      this.input.max = max;
    } else {
      this.input.removeAttribute('max');
    }
    this.input.value = min;
    emitCocoEvent(COCO_EVENTS.quantityUpdated);
  }

  reset() {
    if (!this.input) return;
    this.input.value = this.input.defaultValue;
  }
}

if (!customElements.get(CE_NAME)) {
  customElements.define(CE_NAME, CocoQty);
}
